/**
 * FooterFragment - Renders footer from generated HTML fragment with React hydration
 * 
 * This component:
 * - Imports the generated footer HTML fragment (contains full navigation links)
 * - Transforms static site links (*.html) to public site URLs
 * - Converts internal links to React Router Links for SPA navigation
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import parse, { domToReact } from 'html-react-parser';
import { getPublicSiteUrl } from '../../../config/urls';
import footerHtml from '../../../assets/generated/footer.fragment.html?raw';

const FooterFragment = () => {
  // Get the public site base URL for transforming .html links
  const publicSiteUrl = getPublicSiteUrl();

  // Parser options to handle React integration.
  // Fragment HTML uses "class"; when creating React elements, use className.
  const parserOptions = useMemo(() => ({
    replace: (domNode) => {
      // Handle anchor tags
      if (domNode.name === 'a' && domNode.attribs?.href) {
        const { href, class: className, ...restAttribs } = domNode.attribs;

        // External links (http/https) - keep as anchor
        if (href.startsWith('http://') || href.startsWith('https://')) {
          return (
            <a href={href} className={className} {...restAttribs}>
              {domToReact(domNode.children, parserOptions)}
            </a>
          );
        }

        // Static site page links (*.html) - transform to public site URL
        if (href.endsWith('.html') || href.includes('.html#')) {
          const fullUrl = `${publicSiteUrl}/${href.replace(/^\//, '')}`;
          return (
            <a href={fullUrl} className={className} {...restAttribs}>
              {domToReact(domNode.children, parserOptions)}
            </a>
          );
        }

        // Hash links on same page - keep as anchor
        if (href.startsWith('#')) {
          return (
            <a href={href} className={className} {...restAttribs}>
              {domToReact(domNode.children, parserOptions)}
            </a>
          );
        }

        // Internal app routes (starting with /) - use React Router Link
        if (href.startsWith('/')) {
          return (
            <Link to={href} className={className} {...restAttribs}>
              {domToReact(domNode.children, parserOptions)}
            </Link>
          );
        }

        // Relative links without extension - assume static site page
        const fullUrl = `${publicSiteUrl}/${href}`;
        return (
          <a href={fullUrl} className={className} {...restAttribs}>
            {domToReact(domNode.children, parserOptions)}
          </a>
        );
      }
    }
  }), [publicSiteUrl]);

  return parse(footerHtml, parserOptions);
};

export default FooterFragment;
